// Tool invocation client — wraps POST /api/v1/tools/:slug/invoke.
// Single fetch point for the verb:'invoke' palette flow (FR 2026-05-31).
//
// The server-side proxy (apps/api/src/routes/tools.ts §/tools/:slug/invoke)
// already enforces tag scope, SSRF, runId stamping, schemaVersion
// gating, and audit logging. This wrapper is a thin typed surface:
// callers say "run this action on this document"; we hand back the
// validated ToolInvokeResult.
//
// Composite-key contract: the ToolPalette emits keys like
// `${toolSlug}:${actionKey}` (matching the shape useToolActions
// produces). Use `splitCompositeKey` to split before calling
// `invokeToolAction`.

import type { ToolInvokeResult } from '@captain-tasko/shared';
import { apiSingle } from './api.js';

/** Split a composite palette key into its tool + action parts.
 *  Returns null when the key is unprefixed (a hardcoded mockup button
 *  rather than a tool-contributed action). */
export function splitCompositeKey(
  key: string,
): { toolSlug: string; actionKey: string } | null {
  const i = key.indexOf(':');
  if (i < 0) return null;
  const toolSlug = key.slice(0, i);
  const actionKey = key.slice(i + 1);
  if (!toolSlug || !actionKey) return null;
  return { toolSlug, actionKey };
}

export interface InvokeOptions {
  /** Caller-supplied params forwarded verbatim to the tool. */
  params?: Record<string, unknown>;
  /** When true, the server short-circuits before the upstream call
   *  and returns the projected payload + resolved endpoint in
   *  result.meta. Useful for "what would this send?" smoke tests. */
  dryRun?: boolean;
}

/** Invoke a tool action against a document.
 *
 *  Resolves to the validated `ToolInvokeResult` envelope on 2xx.
 *  Rejects with the standard `AppError`-derived JSON message body on
 *  any non-2xx (the api.ts helpers surface server errors as throw —
 *  callers handle via try/catch + toast).
 */
export async function invokeToolAction(
  toolSlug: string,
  actionKey: string,
  documentId: string,
  opts: InvokeOptions = {},
): Promise<ToolInvokeResult> {
  const body: Record<string, unknown> = {
    actionKey,
    documentId,
  };
  if (opts.params !== undefined) body.params = opts.params;
  if (opts.dryRun) body.dryRun = true;
  return apiSingle<ToolInvokeResult>(`/tools/${encodeURIComponent(toolSlug)}/invoke`, {
    method: 'POST',
    body,
  });
}
